import { useCallback, useEffect, useReducer } from "react";
import {
  GenericPath,
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3DocumentDownload,
  useV3GenericGetData,
  useV3UploadDocument,
} from "src/api/snugtotal/estate_app";
import { useMounted } from "src/hooks/use-mounted";
import { DocumentV2, LegacyObj } from "src/types/snugtotal";
import { toast } from "react-hot-toast";
import { reportError } from "src/utils/error-reporting";
import type { File } from "src/components/file-dropzone";
import { downloadFileFromS3 } from "src/utils/snug";

const subcategoryToUrlParam = (inboundName: string) => {
  return inboundName.replace(/_/g, "-").toLowerCase();
};

interface DocumentSavingStatus {
  [key: string]: "SUCCESS" | "ERROR" | "LOADING" | null;
}

interface DocumentStatusMap {
  finalRestingPlace: DocumentSavingStatus;
  memorialFuneral: DocumentSavingStatus;
  organDonation: DocumentSavingStatus;
  family: DocumentSavingStatus;
  wow: DocumentSavingStatus;
}

// Action Types
type LegacyStoreAction =
  | { type: "SET_LEGACY"; payload: LegacyObj }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_LEGACY_SAVING_NEW"; payload: boolean }
  | { type: "ADD_OR_UPDATE_LEGACY"; payload: LegacyObj }
  | { type: "SET_LEGACY_FILES_TO_UPLOAD"; payload: File[] }
  | {
      type: "SET_DOCUMENT_STATUS_MAP";
      payload: DocumentStatusMap;
    };

const legacyStoreReducer = (
  state: LegacyStoreState,
  action: LegacyStoreAction
): LegacyStoreState => {
  switch (action.type) {
    case "SET_LEGACY":
      return { ...state, legacy: action.payload, loading: false };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_LEGACY_SAVING_NEW":
      return { ...state, savingNew: action.payload };
    case "ADD_OR_UPDATE_LEGACY":
      return { ...state, legacy: action.payload };
    case "SET_LEGACY_FILES_TO_UPLOAD":
      return { ...state, legacyFilesToUpload: action.payload };
    case "SET_DOCUMENT_STATUS_MAP":
      return { ...state, documentStatusMap: action.payload };
    default:
      return state;
  }
};

interface LegacyStoreState {
  legacy: LegacyObj | null;
  loading: boolean;
  savingNew: boolean;
  legacyFilesToUpload: File[];
  documentStatusMap: DocumentStatusMap;
}

export const useLegacyStore = (ud_id: string | null) => {
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(legacyStoreReducer, {
    legacy: null,
    loading: true,
    savingNew: false,
    legacyFilesToUpload: [],
    documentStatusMap: {
      finalRestingPlace: {},
      memorialFuneral: {},
      organDonation: {},
      family: {},
      wow: {},
    },
  });

  const fetchLegacyData = useV3GenericGetData<LegacyObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createLegacy = useV3Create<LegacyObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateLegacy = useV3Update<LegacyObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.LEGACY_DOCUMENTS_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<LegacyObj>(
    GenericPath.LEGACY_DOCUMENTS_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.LEGACY_DOCUMENTS_DELETE_V3
  );

  //DOCUMENTS

  const handleLegacyFileUpload = useCallback(
    async (legacyDocumentSubcategory: string, entityId: string) => {
      if (!ud_id || !entityId || !legacyDocumentSubcategory) {
        return Promise.reject("Missing Data for upload.");
      }
      dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: true });

      const files = state.legacyFilesToUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "legacy",
              entity_id: entityId,
              legacy_documents_subcategory: subcategoryToUrlParam(
                legacyDocumentSubcategory
              ),
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "SET_LEGACY",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_LEGACY_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        } finally {
          dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: false });
        }
      }
    },
    [state.legacyFilesToUpload, ud_id, uploadDocument]
  );

  const handleDownloadLegacyDocument = async (
    document_id: string,
    entity_id: string,
    legacyDocumentSubcategory: string
  ) => {
    if (!ud_id || !document_id || !entity_id) {
      return Promise.reject("Missing Data for download.");
    }

    toast.success("Downloading file...");

    const params: any = {
      ud_id: ud_id,
      entity: "legacy",
      entity_id: entity_id,
      document_id: document_id,
      legacy_documents_subcategory: subcategoryToUrlParam(
        legacyDocumentSubcategory
      ),
    };

    const getDocumentName = (document_id: string) => {
      // loop through each document category on the legacy object and get the document name with the corresponding document_id
      const legacy = state.legacy;
      if (!legacy) {
        return "";
      }

      const documentCategories = [
        "final_resting_place_documents",
        "funeral_memorial_documents",
        "organ_donation_documents",
        "family_documents",
        "wow_documents",
      ];

      for (const category of documentCategories) {
        const documents = legacy[category as keyof LegacyObj] as DocumentV2[];
        if (documents) {
          const document = documents.find(
            (doc: DocumentV2) => doc.document_id === document_id
          );
          if (document) {
            return document.document_name;
          }
        }
      }
      return "Download.error";
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in legacy objects' documents array
          downloadFileFromS3(
            res,
            document_id ? getDocumentName(document_id) : "Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document url for entity_id: ${entity_id} and document_id: ${document_id}`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document url for entity_id: ${entity_id} and document_id: ${document_id} with message "${err}"`));
      });
  };

  const handleDeleteLegacyDocument = useCallback(
    async (
      entity_id: string,
      documentId: string,
      legacyDocumentSubcategory: string
    ) => {
      if (!documentId || !entity_id || !legacyDocumentSubcategory || !ud_id) {
        return Promise.reject("Missing Data for delete.");
      }
      try {
        dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id,
          entity: "legacy",
          entity_id: entity_id,
          document_id: documentId,
          legacy_documents_subcategory: subcategoryToUrlParam(
            legacyDocumentSubcategory
          ),
        });

        //hand resting place subcategory
        const subCat =
          legacyDocumentSubcategory === "resting_place"
            ? "final_resting_place"
            : legacyDocumentSubcategory;

        // update the state object by removing the deleted document
        const updatedLegacy = { ...state.legacy } as LegacyObj;
        const category = `${subCat}_documents` as keyof LegacyObj;

        // Create a partial update with the correct type assertion
        const partialUpdate: Partial<LegacyObj> = {
          [category]: (updatedLegacy[category] as DocumentV2[]).filter(
            (doc) => doc.document_id !== documentId
          ),
        };

        // Apply the partial update to the updatedLegacy object
        Object.assign(updatedLegacy, partialUpdate);
        dispatch({ type: "SET_LEGACY", payload: updatedLegacy });

        toast.success("Document removed successfully");
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.legacy, ud_id, deleteDocumentById]
  );

  const handleLegacyFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_LEGACY_FILES_TO_UPLOAD",
        payload: [...state.legacyFilesToUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.legacyFilesToUpload]
  );

  const handleLegacyFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.legacyFilesToUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_LEGACY_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.legacyFilesToUpload]
  );

  const handleLegacyFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_LEGACY_FILES_TO_UPLOAD", payload: [] });
  }, []);

  //// END DOCUMENTS FUNCTIONS

  const fetchLegacyItems = useCallback(async () => {
    if (!ud_id) {
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    try {
      const response = await fetchLegacyData({
        ud_id: ud_id, //bleh. REFACTOR IN v3
        entity: "legacy",
      });

      if (isMounted()) {
        dispatch({ type: "SET_LEGACY", payload: response });
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [ud_id, fetchLegacyData, isMounted]);

  const handleAddOrUpdateLegacySingleCall = useCallback(
    async (legacyItem: LegacyObj): Promise<LegacyObj | null> => {
      if (!ud_id || !legacyItem) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(legacyItem.id);
        let response;
        if (isUpdate) {
          response = await updateLegacy(
            {
              ud_id: ud_id,
              entity: "legacy",
              entity_id: legacyItem.id || undefined,
            },
            legacyItem,
            false
          );
        } else {
          response = await createLegacy(
            {
              ud_id: ud_id,
              entity: "legacy",
            },
            legacyItem
          );
        }

        dispatch({ type: "ADD_OR_UPDATE_LEGACY", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save legacy item");
        console.error(error);
        return null;
      }
    },
    [createLegacy, updateLegacy, ud_id]
  );

  const handleAddOrUpdateLegacy = useCallback(
    async (legacyItem: LegacyObj, legacyItemSubcategory: string) => {
      dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: true });
      if (state.legacyFilesToUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateLegacySingleCall(legacyItem);
          if (res) {
            await handleLegacyFileUpload(legacyItemSubcategory, res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error("Failed to add or edit legacy data. Please try again");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateLegacySingleCall(legacyItem);
        } catch (error) {
          toast.error("Failed to add or edit legacy data. Please try again");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_LEGACY_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateLegacySingleCall,
      state.legacyFilesToUpload,
      handleLegacyFileUpload,
    ]
  );

  useEffect(() => {
    if (ud_id) {
      fetchLegacyItems(); // Fetch all legacy items
    }
  }, [fetchLegacyItems, ud_id]);

  return {
    ...state,
    handleAddOrUpdateLegacy,
    handleDeleteLegacyDocument,
    handleDownloadLegacyDocument,
    handleLegacyFileRemove,
    handleLegacyFilesDrop,
    handleLegacyFilesRemoveAll,
    handleLegacyFileUpload,
  };
};
